/**
 * +glide: free flight. A unit in the air (Unit.jump.air) has an absolute height; it glides (slow
 * sink, forward drift, steered by the held keys), falls (glider folded, a hop off a platform), rides
 * updrafts, flies over obstacles lower than itself, and lands on the terrain floor or a platform.
 * Platforms (Unit.perch) are walkable discs at a height; stepping off one is a fall.
 *
 * Rules (docs/design/zones.md "Gliding"):
 *  - horizontally a flyer may enter a spot inside the map's walkable areas whose floor is at most
 *    GLIDE.step above it and whose obstacles' tops (floor + AirspaceDef.obstacleTops[i]; missing =
 *    solid) are below it; otherwise it slides along one axis or stops (and sinks down the face);
 *  - it lands when its height reaches the floor (or a platform top it was above); a landing on
 *    terrain that is not walkable ground connected to the start (deep water, a cliff band, a pocket)
 *    is moved to the nearest such ground (nav landingSpot), so a flyer can never strand itself.
 * Pure functions of the world + content; deterministic.
 */
import type { AirState, AirspaceDef, ContentRegistry, PlatformDef, SimContext, Unit, UpdraftDef, Vec2 } from '../contracts';
import { GLIDE, JUMP_VZ, airFloorFn, jumpHeightAt, jumpRateAt } from '../contracts';
import { backpedalFactor } from './geometry';
import { WALL_RADIUS, circleWalkable, insideArea, landingSpot, obstacleIdsAt, reachMask, type NavData } from './nav';

const EPS = 1e-6;

export function airspaceOf(content: Pick<ContentRegistry, 'dungeon'>, dungeonId: string): AirspaceDef | null {
  return content.dungeon(dungeonId)?.airspace ?? null;
}

/** Terrain / water floor at p (0 without an airspace or a registered floor). */
export function floorAt(as: AirspaceDef | null, p: Vec2): number {
  const fn = as ? airFloorFn(as.floor) : null;
  return fn ? fn(p.x, p.y) : 0;
}

const onPlatform = (pl: PlatformDef, p: Vec2): boolean => (p.x - pl.pos.x) ** 2 + (p.y - pl.pos.y) ** 2 <= pl.r * pl.r;

/** The platform whose top is (exactly) `z` under p, or null. */
export function platformUnder(as: AirspaceDef | null, p: Vec2, z: number): PlatformDef | null {
  for (const pl of as?.platforms ?? []) if (Math.abs(pl.top - z) < 1e-6 && onPlatform(pl, p)) return pl;
  return null;
}

/** What a flyer at p that was at height `from` stands on: the floor, or the highest platform top at or below `from`. */
export function supportAt(as: AirspaceDef | null, p: Vec2, from: number): { z: number; platform: PlatformDef | null } {
  let z = floorAt(as, p);
  let platform: PlatformDef | null = null;
  for (const pl of as?.platforms ?? []) if (pl.top > z && pl.top <= from + 1e-6 && onPlatform(pl, p)) (z = pl.top), (platform = pl);
  return { z, platform };
}

/** The updraft column holding a flyer at (p, z), or null. */
export function updraftAt(as: AirspaceDef | null, p: Vec2, z: number): UpdraftDef | null {
  for (const u of as?.updrafts ?? []) if (z >= u.base - 1 && z <= u.top + 1.5 && (p.x - u.pos.x) ** 2 + (p.y - u.pos.y) ** 2 <= u.r * u.r) return u;
  return null;
}

/** A unit's height (yd): free flight, a platform, a hop over the floor, or the floor. */
export function unitHeight(as: AirspaceDef | null, u: Pick<Unit, 'pos' | 'jump' | 'perch'>, timeMs: number): number {
  if (u.jump?.air) return u.jump.air.z;
  if (u.perch) return u.perch.z;
  const f = floorAt(as, u.pos);
  return u.jump ? f + jumpHeightAt(u.jump, timeMs) : f;
}

/** A flyer at height z may occupy p. */
export function airOk(n: NavData, as: AirspaceDef, p: Vec2, z: number): boolean {
  if (!insideArea(n, p)) return false;
  const f = floorAt(as, p);
  if (f > z + GLIDE.step) return false;
  for (const i of obstacleIdsAt(n, p)) {
    const top = as.obstacleTops?.[i];
    if (top === undefined || f + top > z) return false;
  }
  return true;
}

/** Air move from→to at height z: the full move, else one axis, else stay (which axes were blocked). */
export function airCollide(n: NavData, as: AirspaceDef | null, from: Vec2, to: Vec2, z: number): { pos: Vec2; bx: boolean; by: boolean } {
  const ok = (p: Vec2): boolean => (as ? airOk(n, as, p, z) : circleWalkable(n, p, WALL_RADIUS));
  if (ok(to)) return { pos: to, bx: false, by: false };
  const sx = { x: to.x, y: from.y };
  if (Math.abs(to.x - from.x) > EPS && ok(sx)) return { pos: sx, bx: false, by: true };
  const sy = { x: from.x, y: to.y };
  if (Math.abs(to.y - from.y) > EPS && ok(sy)) return { pos: sy, bx: true, by: false };
  return { pos: from, bx: true, by: true };
}

/** The move direction the unit's keys ask for now (a mid-air move waits in `pending`). */
function intentDir(u: Unit): Vec2 | null {
  const pd = u.jump?.pending;
  const norm = (v: Vec2): Vec2 | null => {
    const l = Math.hypot(v.x, v.y);
    return l > EPS ? { x: v.x / l, y: v.y / l } : null;
  };
  if (pd) {
    if (pd.stop) return null;
    if (pd.dir) return norm(pd.dir);
    if (pd.dest) return norm({ x: pd.dest.x - u.pos.x, y: pd.dest.y - u.pos.y });
    return null;
  }
  const m = u.move;
  if (m?.mode === 'dir') return norm(m.dir);
  if (m?.mode === 'dest') {
    const wp = m.path[m.pathIndex] ?? m.dest;
    return norm({ x: wp.x - u.pos.x, y: wp.y - u.pos.y });
  }
  return null;
}

/** The unit's ground velocity right now (its run), for momentum into the air. */
export function groundVelocity(ctx: SimContext, u: Unit): Vec2 {
  const m = u.move;
  if (!m || m.mode === 'forced' || !ctx.sys.combat.controlState(ctx, u.id).canMove) return { x: 0, y: 0 };
  const speed = ctx.sys.combat.effectiveStats(ctx, u.id).moveSpeed;
  if (m.mode === 'dir') {
    const l = Math.hypot(m.dir.x, m.dir.y);
    if (l <= EPS) return { x: 0, y: 0 };
    const k = (speed * backpedalFactor(u.controller, m.dir, u.facing)) / l;
    return { x: m.dir.x * k, y: m.dir.y * k };
  }
  const wp = m.path[m.pathIndex] ?? m.dest;
  const dx = wp.x - u.pos.x;
  const dy = wp.y - u.pos.y;
  const l = Math.hypot(dx, dy);
  return l > EPS ? { x: (dx / l) * speed, y: (dy / l) * speed } : { x: 0, y: 0 };
}

/** A classic hop becomes free flight (glider opening, an updraft catching it). */
export function hopToAir(ctx: SimContext, u: Unit, glide: boolean): AirState | null {
  const j = u.jump;
  if (!j) return null;
  if (j.air) return j.air;
  const as = airspaceOf(ctx.content, ctx.world.dungeonId);
  const z = floorAt(as, u.pos) + jumpHeightAt(j, ctx.now);
  j.air = { z, vz: ctx.now < j.until ? jumpRateAt(j, ctx.now) : 0, vel: groundVelocity(ctx, u), glide, peak: z };
  return j.air;
}

/** A hop from a platform (free flight from the start: it may land on another platform or fall). */
export function hopFromPerch(ctx: SimContext, u: Unit, untilMs: number): void {
  const z = u.perch!.z;
  u.jump = { at: ctx.now, until: untilMs, pending: null, air: { z, vz: JUMP_VZ, vel: groundVelocity(ctx, u), glide: false, peak: z } };
  delete u.perch;
}

/** Stepped off a platform: fall (momentum kept). */
function fallFromPerch(ctx: SimContext, u: Unit, vel: Vec2): void {
  const z = u.perch!.z;
  u.jump = { at: ctx.now, until: ctx.now, pending: null, air: { z, vz: 0, vel, glide: false, peak: z } };
  delete u.perch;
}

/** Touch down at the current spot (support `sup`): a platform perch, else resolved walkable ground. */
function touchDown(ctx: SimContext, n: NavData, as: AirspaceDef | null, u: Unit, sup: { z: number; platform: PlatformDef | null }): void {
  const a = u.jump!.air!;
  a.landed = true;
  a.vz = 0;
  if (sup.platform) {
    a.z = sup.platform.top;
    u.perch = { z: sup.platform.top, platformId: sup.platform.id };
    return;
  }
  const entrance = ctx.content.dungeon(ctx.world.dungeonId)?.entrance ?? u.pos;
  const spot = landingSpot(n, as ? reachMask(n, entrance) : null, u.pos);
  u.pos = spot;
  a.z = floorAt(as, spot);
}

/** Drop a dead / teleported flyer or percher straight to valid ground. */
export function groundUnit(ctx: SimContext, n: NavData, u: Unit): void {
  const as = airspaceOf(ctx.content, ctx.world.dungeonId);
  if (u.perch) {
    delete u.perch;
    const entrance = ctx.content.dungeon(ctx.world.dungeonId)?.entrance ?? u.pos;
    u.pos = landingSpot(n, as ? reachMask(n, entrance) : null, u.pos);
  }
  if (u.jump?.air && !u.jump.air.landed) touchDown(ctx, n, as, u, { z: floorAt(as, u.pos), platform: null });
}

/** One tick of free flight for `u` (core tick step 5, in spatial.moveUnits). */
export function stepAir(ctx: SimContext, n: NavData, u: Unit, dtMs: number): void {
  const j = u.jump!;
  const a = j.air!;
  if (a.landed) return;
  const as = airspaceOf(ctx.content, ctx.world.dungeonId);
  const s = dtMs / 1000;
  const forced = u.move?.mode === 'forced' ? u.move : null;
  const canMove = ctx.sys.combat.controlState(ctx, u.id).canMove;
  const fold = (): void => {
    if (!a.glide) return;
    a.glide = false;
    a.folded = true;
    ctx.emit({ type: 'glide', sourceID: u.id, on: false });
  };
  // A dodge / knockback, a stun or a root folds the glider.
  if (forced || !canMove) fold();
  const ud = updraftAt(as, u.pos, a.z);
  // An updraft opens the glider of a hero that flies into it (bots never glide).
  if (ud && !a.glide && !forced && canMove && u.kind === 'player' && u.controller === 'human') {
    a.glide = true;
    delete a.folded;
    ctx.emit({ type: 'glide', sourceID: u.id, on: true });
  }

  if (a.glide) {
    const d = intentDir(u);
    let tx: number;
    let ty: number;
    if (ud) {
      // Hover in the column: drift only as the keys ask, pulled gently toward its axis.
      // (The pull fades to nothing at the rim, so any key held outward leaves the column.)
      const sp = GLIDE.speed * GLIDE.chuteSpeed;
      const rim = Math.max(0, 1 - Math.hypot(ud.pos.x - u.pos.x, ud.pos.y - u.pos.y) / ud.r);
      tx = (d ? d.x * sp : 0) + (ud.pos.x - u.pos.x) * GLIDE.chutePull * rim;
      ty = (d ? d.y * sp : 0) + (ud.pos.y - u.pos.y) * GLIDE.chutePull * rim;
    } else {
      const fx = Math.cos(u.facing);
      const fy = Math.sin(u.facing);
      const rx = Math.sin(u.facing);
      const ry = -Math.cos(u.facing);
      const fwd = d ? d.x * fx + d.y * fy : 0;
      const lat = d ? d.x * rx + d.y * ry : 0;
      const sp = GLIDE.speed + (fwd > 0 ? fwd * GLIDE.boost : fwd * GLIDE.brake);
      tx = fx * sp + rx * lat * GLIDE.strafe;
      ty = fy * sp + ry * lat * GLIDE.strafe;
    }
    const k = Math.min(1, GLIDE.accel * s);
    a.vel = { x: a.vel.x + (tx - a.vel.x) * k, y: a.vel.y + (ty - a.vel.y) * k };
    const vzT = ud ? GLIDE.lift * Math.max(0, Math.min(1, (ud.top - a.z) / GLIDE.liftEase)) : -GLIDE.sink;
    a.vz += (vzT - a.vz) * Math.min(1, GLIDE.vAccel * s);
  } else {
    a.vz = Math.max(a.vz - GLIDE.gravity * s, -GLIDE.fallMax);
    if (a.folded) {
      const k = Math.max(0, 1 - GLIDE.fallDrag * s);
      a.vel = { x: a.vel.x * k, y: a.vel.y * k };
    }
  }

  // Horizontal: the flight velocity, or the forced move (dash / knockback) pushing through the air.
  let want: Vec2;
  if (forced) {
    const dx = forced.to.x - u.pos.x;
    const dy = forced.to.y - u.pos.y;
    const dd = Math.hypot(dx, dy);
    const step = forced.speed <= 0 ? dd : (forced.speed * dtMs) / 1000;
    want = dd <= step ? { x: forced.to.x, y: forced.to.y } : { x: u.pos.x + (dx / dd) * step, y: u.pos.y + (dy / dd) * step };
  } else want = { x: u.pos.x + a.vel.x * s, y: u.pos.y + a.vel.y * s };
  const z1 = a.z + a.vz * s;
  const from = a.z;
  if (Math.abs(want.x - u.pos.x) > EPS || Math.abs(want.y - u.pos.y) > EPS) {
    const r = airCollide(n, as, u.pos, want, Math.max(from, z1));
    if (r.bx) a.vel = { x: 0, y: a.vel.y };
    if (r.by) a.vel = { x: a.vel.x, y: 0 };
    if (forced && r.bx && r.by) forced.to = { ...u.pos };
    u.pos = r.pos;
  }
  if (forced && ctx.now >= forced.until) {
    u.move = null;
    // The push becomes drift (halved) so the fall carries on the way it was going.
    if (forced.speed > 0) {
      const dx = forced.to.x - u.pos.x;
      const dy = forced.to.y - u.pos.y;
      const dd = Math.hypot(dx, dy);
      if (dd > EPS) a.vel = { x: (dx / dd) * forced.speed * 0.25, y: (dy / dd) * forced.speed * 0.25 };
    }
  }
  a.z = z1;
  if (a.z > a.peak) a.peak = a.z;
  const sup = supportAt(as, u.pos, from);
  if (a.z <= sup.z) touchDown(ctx, n, as, u, sup);
}

/**
 * After a ground move: a classic hop that comes down onto a platform lands on it, one that enters an
 * updraft becomes a glide; a hero walking off a platform's edge falls.
 */
export function afterGroundMove(ctx: SimContext, n: NavData, u: Unit, dtMs: number, prevPos: Vec2): void {
  const as = airspaceOf(ctx.content, ctx.world.dungeonId);
  if (!as) return;
  if (u.perch) {
    if (!platformUnder(as, u.pos, u.perch.z)) fallFromPerch(ctx, u, { x: ((u.pos.x - prevPos.x) * 1000) / dtMs, y: ((u.pos.y - prevPos.y) * 1000) / dtMs });
    return;
  }
  const j = u.jump;
  if (!j || j.air || ctx.now >= j.until + dtMs) return;
  const f = floorAt(as, u.pos);
  const h = jumpHeightAt(j, ctx.now);
  const hPrev = jumpHeightAt(j, ctx.now - dtMs);
  const z = f + h;
  if (u.kind === 'player' && u.controller === 'human' && updraftAt(as, u.pos, z) && ctx.sys.combat.controlState(ctx, u.id).canMove) {
    hopToAir(ctx, u, true);
    ctx.emit({ type: 'glide', sourceID: u.id, on: true });
    return;
  }
  if (h >= hPrev && ctx.now < j.until) return; // still rising
  const sup = supportAt(as, u.pos, f + hPrev);
  if (sup.platform && sup.z >= z - 1e-6) {
    // Caught by a platform on the way down: land on it this tick.
    u.perch = { z: sup.platform.top, platformId: sup.platform.id };
    j.until = ctx.now;
  }
}

/** Moving on a platform: collide in the air at the platform's height (it may overhang obstacles). */
export function perchCollide(n: NavData, ctx: SimContext, u: Unit, from: Vec2, to: Vec2): Vec2 {
  return airCollide(n, airspaceOf(ctx.content, ctx.world.dungeonId), from, to, u.perch!.z).pos;
}
